// Booking keypair validity + self-heal helpers.
//
// The patient booking web app hybrid-encrypts patient details against
// `chains/{chainId}.bookingPublicKey`. Pharmacists decrypt them by unwrapping
// `chains/{chainId}.encryptedBookingPrivateKey` with the chain AES key. The
// pair can go half-valid — public key present, wrapped private key missing,
// or wrapped against a chain key that has since rotated — and nothing ever
// re-checks that after the first time it's written. These helpers classify
// that state and, on the admin side, regenerate+rewrite the pair atomically.
//
// Pure module: no Firebase/React imports so it can be shared between the
// mobile app (read-only diagnosis) and the web admin panels (self-heal), and
// unit-tested directly with WebCrypto (available globally in Node >= 20).

import {
  decrypt,
  encrypt,
  exportPrivateKeyAsJwk,
  exportPublicKey,
  generateBookingKeypair,
  hybridDecryptWithPrivateKey,
  hybridEncryptForPublicKey,
  importPrivateKeyFromJwk,
  importPublicKey,
} from "./crypto";

// Fixed probe string round-tripped through hybridEncryptForPublicKey /
// hybridDecryptWithPrivateKey to confirm bookingPublicKey and the unwrapped
// private key are actually the two halves of the same keypair — not just
// individually well-formed.
const KEY_CORRESPONDENCE_PROBE = "ccs-booking-keypair-correspondence-probe";

/** Distinct, loggable reasons a booking keypair fails to resolve to a usable private key. */
export type BookingKeypairInvalidReason =
  // Neither field is set — the chain has never had a booking keypair generated.
  | "no-key-material"
  // bookingPublicKey is missing/empty/malformed while a wrapped private key
  // exists — a partial write, or the public field got corrupted separately.
  | "public-key-invalid"
  // bookingPublicKey exists but encryptedBookingPrivateKey does not — a
  // partial write, or a key that was published without ever wrapping the
  // private half.
  | "wrapped-key-missing"
  // encryptedBookingPrivateKey exists but AES-decrypting it with the current
  // chain key fails — almost always a stale wrap from before a chain-key
  // rotation.
  | "unwrap-failed"
  // The unwrapped JWK does not import as an RSA-OAEP private key — corrupt
  // ciphertext or a shape mismatch.
  | "import-failed"
  // The private key imported fine, but it does not correspond to
  // bookingPublicKey (round-trip encrypt-with-public/decrypt-with-private
  // fails) — the two halves were written from different keypairs, e.g. a
  // partial regeneration that only updated one field.
  | "keys-mismatched";

export type BookingKeypairValidity =
  | { valid: true }
  | { valid: false; reason: BookingKeypairInvalidReason };

export type BookingKeypairUnwrapResult =
  | { ok: true; privateKey: CryptoKey }
  | { ok: false; reason: BookingKeypairInvalidReason };

export interface BookingKeypairChainFields {
  bookingPublicKey?: string | null;
  encryptedBookingPrivateKey?: string | null;
}

/**
 * Attempt to resolve a usable booking private key from a chain doc's fields
 * and the chain's current AES key. Classifies exactly why it failed when it
 * does, instead of collapsing every cause into one generic failure.
 */
export async function unwrapBookingPrivateKey(
  chainData: BookingKeypairChainFields,
  chainKey: CryptoKey,
): Promise<BookingKeypairUnwrapResult> {
  if (!chainData.bookingPublicKey && !chainData.encryptedBookingPrivateKey) {
    return { ok: false, reason: "no-key-material" };
  }
  if (!chainData.encryptedBookingPrivateKey) {
    return { ok: false, reason: "wrapped-key-missing" };
  }
  // A wrapped private key with no (or an unimportable) public key is just as
  // broken as the reverse — the booking web app has nothing valid to encrypt
  // against, or what it has doesn't match what we're about to unwrap.
  let publicKey: CryptoKey;
  try {
    if (!chainData.bookingPublicKey) throw new Error("missing public key");
    publicKey = await importPublicKey(chainData.bookingPublicKey);
  } catch {
    return { ok: false, reason: "public-key-invalid" };
  }

  let privateKeyJwk: string;
  try {
    privateKeyJwk = await decrypt(chainData.encryptedBookingPrivateKey, chainKey);
  } catch {
    return { ok: false, reason: "unwrap-failed" };
  }

  let privateKey: CryptoKey;
  try {
    privateKey = await importPrivateKeyFromJwk(privateKeyJwk);
  } catch {
    return { ok: false, reason: "import-failed" };
  }

  // Confirm publicKey and privateKey are actually the two halves of the same
  // pair — a corrupted/overwritten single field otherwise still "unwraps"
  // and "imports" fine while decrypting nothing pharmacists can use.
  try {
    const probeBlob = await hybridEncryptForPublicKey(publicKey, KEY_CORRESPONDENCE_PROBE);
    const roundTripped = await hybridDecryptWithPrivateKey(privateKey, probeBlob);
    if (roundTripped !== KEY_CORRESPONDENCE_PROBE) {
      return { ok: false, reason: "keys-mismatched" };
    }
  } catch {
    return { ok: false, reason: "keys-mismatched" };
  }

  return { ok: true, privateKey };
}

/**
 * Same classification as {@link unwrapBookingPrivateKey}, without returning
 * the key itself — for callers (e.g. admin self-heal, health-check scripts)
 * that only need to know whether the pair is currently usable.
 */
export async function validateBookingKeypair(
  chainData: BookingKeypairChainFields,
  chainKey: CryptoKey,
): Promise<BookingKeypairValidity> {
  const result = await unwrapBookingPrivateKey(chainData, chainKey);
  return result.ok ? { valid: true } : { valid: false, reason: result.reason };
}

/**
 * Generate a fresh booking RSA keypair and wrap the private half with the
 * given chain AES key, ready to be written atomically to both
 * `bookingPublicKey` and `encryptedBookingPrivateKey` on the chain doc.
 *
 * Callers MUST write both returned fields together in a single update — a
 * public key with no matching wrapped private key (or vice versa) recreates
 * exactly the half-valid state this module exists to detect.
 */
export async function generateBookingKeypairFields(
  chainKey: CryptoKey,
): Promise<{ bookingPublicKey: string; encryptedBookingPrivateKey: string }> {
  const keypair = await generateBookingKeypair();
  const bookingPublicKey = await exportPublicKey(keypair.publicKey);
  const privateKeyJwk = await exportPrivateKeyAsJwk(keypair.privateKey);
  const encryptedBookingPrivateKey = await encrypt(privateKeyJwk, chainKey);
  return { bookingPublicKey, encryptedBookingPrivateKey };
}
